// TMUA Specialty Landing Page — Editorial redesign
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import TmuaEditorialFooter from './TmuaEditorialFooter';
import './TmuaLandingPage.css';

import { getBaseApiUrl, getEduPortalUrl } from '../../../config/api';
import { consentAllows } from '../../consent/consentStore';
import { checkoutRefusalMessage, showCheckoutRefusal } from '../../checkout/checkoutRefusal';
import { ORG_PLAN_NOTICE, useStoredUserMayPurchase } from '../../checkout/purchaseEligibility';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the admissions matrix, method cards, sample
// question and school case-study sections).
function ScrollCarousel({ children, ariaLabel }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="tmua-scroll-carousel">
      <button
        type="button"
        className={`tmua-carousel-arrow tmua-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`tmua-carousel-arrow tmua-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="tmua-card-scroll" ref={scrollRef}>
        <div className="tmua-card-track">
          {children}
        </div>
      </div>
      <div className="tmua-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function TmuaLandingPage() {
  const navigate = useNavigate();
  const mayPurchase = useStoredUserMayPurchase();
  const [activeFaq, setActiveFaq] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');

  useEffect(() => {
    if (window.gtag && consentAllows('advertising')) {
      window.gtag('config', 'AW-18348840584', {
        page_title: 'TMUA Exam Prep 2026',
        page_location: window.location.href
      });
    }
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // Preserved from the previous implementation — the new design's structure
  // cards don't include a "Practice Paper X Mock" CTA, so this modal isn't
  // currently opened by anything below, same as before my changes.
  const handleStartFree = () => {
    setShowLeadModal(true);
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadEmail || !leadEmail.includes('@')) return;
    sessionStorage.setItem('ivori_lead_email', leadEmail);
    sessionStorage.setItem('ivori_lead_exam', 'TMUA');
    setShowLeadModal(false);
    window.location.href = getEduPortalUrl('/practice/tmua');
  };

  const handleExplorePlans = () => {
    const el = document.getElementById('pricing') || document.getElementById('tmua-pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubscribe = async (tierName) => {
    const tierSlug = tierName.toLowerCase().includes('beyond') ? 'tmua_beyond' : (tierName.toLowerCase().includes('foundation') ? 'tmua_foundations' : 'tmua_beginnings');

    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');
    const userRaw = localStorage.getItem('pathbuddy_user') || sessionStorage.getItem('pathbuddy_user') || localStorage.getItem('user');
    const user = (token && userRaw) ? (typeof userRaw === 'string' && userRaw.startsWith('{') ? JSON.parse(userRaw) : { email: userRaw }) : null;

    if (!user || !user.email) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'tmua');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=tmua`);
      return;
    }

    if (tierSlug === 'tmua_beginnings') {
      window.location.href = getEduPortalUrl('/practice/tmua');
      return;
    }

    try {
      const apiUrl = getBaseApiUrl();
      const token2 = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');

      const headers = { 'Content-Type': 'application/json' };
      if (token2) {
        headers['Authorization'] = `Bearer ${token2}`;
      }

      const res = await fetch(`${apiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tier_name: tierSlug,
          exam_type: 'tmua',
          user_id: user?.id,
          email: user?.email
        })
      });
      const data = await res.json().catch(() => ({}));
      const refusal = checkoutRefusalMessage(res, data);
      if (refusal) {
        showCheckoutRefusal(refusal);
        return;
      }
      if (data.checkoutUrl || data.url) {
        window.location.href = data.checkoutUrl || data.url;
      } else {
        sessionStorage.setItem('pending_checkout_tier', tierSlug);
        sessionStorage.setItem('pending_checkout_exam', 'tmua');
        navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=tmua`);
      }
    } catch (err) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'tmua');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=tmua`);
    }
  };

  const admissionsRequirements = [
    { uni: "University of Cambridge", course: "Computer Science (BA)", requirement: "Mandatory", target: "6.5+ / 9.0", details: "Used alongside CSAT/interview shortlisting" },
    { uni: "University of Oxford", course: "Mathematics & CS", requirement: "Highly Recommended", target: "6.5+ / 9.0", details: "Used alongside MAT evaluation" },
    { uni: "Imperial College London", course: "Computer Science & Mathematics", requirement: "Mandatory", target: "6.5+ / 9.0", details: "Determines offer & interview eligibility" },
    { uni: "London School of Economics (LSE)", course: "BSc Economics / Data Science", requirement: "Highly Recommended", target: "6.0+ / 9.0", details: "Differentiates top applicants in selection" },
    { uni: "University of Warwick", course: "BSc Mathematics / Computer Science", requirement: "Highly Recommended", target: "6.5+ / 9.0", details: "Unlocks lower A-level grade requirement" },
    { uni: "Durham University", course: "BSc Mathematics & Data Science", requirement: "Recommended", target: "6.0+ / 9.0", details: "Evaluated for STEM candidate selection" },
    { uni: "University of Bath", course: "BSc Mathematics & Computer Science", requirement: "Recommended", target: "6.0+ / 9.0", details: "Unlocks alternative reduced grade offer" },
    { uni: "University of Sheffield", course: "BSc Computer Science", requirement: "Recommended", target: "5.5+ / 9.0", details: "Strengthens quantitative application score" },
    { uni: "University of Southampton", course: "BSc Mathematics & Software Eng", requirement: "Recommended", target: "5.5+ / 9.0", details: "Additional quantitative indicator" },
    { uni: "Lancaster University", course: "BSc Mathematics & Data Analytics", requirement: "Recommended", target: "5.5+ / 9.0", details: "Secondary quantitative benchmark" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained to reason in line with Pólya's heuristic problem-solving method and Lakatos's logic of proof and refutation, so it evaluates a solution the way a mathematician actually would",
        "Finds exactly where a line of reasoning stops working, so you correct the actual logical gap",
        <>The world's first <button type="button" className="tmua-inline-link tmua-link-btn" onClick={() => navigate('/method/tmua')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="tmua-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "Two Foundations, One Discipline",
      title: "Pólya and Lakatos",
      meta: "Trained on Both Deliberately",
      items: [
        "Multiple choice is a Pólya problem, so you train the exact recognition-of-method it tests, meaning the skill holds up on a problem you've never practiced on",
        "A proof is a Lakatos exercise, so you learn to find or rule out the counterexample that would break it, meaning your answer survives being challenged on any one deduction",
        "Trains on the disciplines mathematicians actually use, targeting the reasoning skill being tested and what the paper actually rewards"
      ]
    },
    {
      tag: "Foundation I",
      title: "Pólya's Method",
      meta: "Recognize the Load-Bearing Technique",
      items: [
        "You understand the problem before touching it, so you build the habit of naming what's given and what's actually being asked",
        "You devise a plan by asking which known technique the solution depends on, exactly the recognition Paper 1 tests under time pressure",
        "You look back once you're done, so a right answer isn't luck — it's a method you can name and reuse on the next unfamiliar problem"
      ]
    },
    {
      tag: "Foundation II",
      title: "Lakatos's Logic",
      meta: "Find or Rule Out the Counterexample",
      items: [
        "Every proof is tested against a counterexample, so the reasoning skill transfers to any argument, not just the ones you've already seen",
        "Spot the one condition a claim can't survive without, so you're practicing the actual mechanism Paper 2 rewards, not a proxy for it",
        "Reasoning tested privately, one student at a time, so the rigor of the method comes without the anxiety of defending a proof in front of a room of peers"
      ]
    },
    {
      tag: "Apply to TMUA Paper 1",
      title: "Mathematical Applications",
      meta: "Answer 20 questions in 75 minutes",
      items: [
        "Every problem is treated as a case — so the skill transfers to questions you've never seen, not just the ones you've drilled",
        "Every wrong answer is traced back to the exact calculation or setup error that caused it, meaning the same mistake doesn't recur on exam day",
        "Diagnostics show which underlying technique needs work, so revision closes the actual gap instead of re-drilling what you already know"
      ]
    },
    {
      tag: "Apply to TMUA Paper 2",
      title: "Mathematical Reasoning",
      meta: "Answer 20 questions in 75 minutes",
      items: [
        "Graded the way a proof is checked — validating each logical step, so you learn to justify a deduction instead of just picking a letter",
        "Assumption, negation and conclusion scored separately, so you know precisely which step of the argument needs work rather than guessing from one overall score",
        "Feedback probes your weakest logical step specifically, so revision targets the exact point where the proof breaks down"
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Dom Bird · Deputy Head Teacher",
      body: "King Edward VI Handsworth Grammar School for Boys has used IVORI for almost two years, supporting Year 12 with admissions test preparation — including the TMUA — problem-set practice, interview preparation and UCAS personal statement support.",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Paul Mann · Head of Careers",
      body: "Piloted IVORI with Year 12 students transitioning into Year 13, preparing them for admissions tests and interviews across the full application cycle — with particular focus on the Oxbridge cohort sitting the TMUA.",
      detailLabel: "Cohort",
      detailValue: "Year 12 → Year 13",
      detail2Label: "Focus",
      detail2Value: "Oxbridge Admissions"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Building on the existing relationship with Handsworth Grammar, IVORI is in active discussions with the wider King Edward VI Foundation about extending TMUA preparation across its family of Birmingham grammar schools.",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "Deployed across schools within the grammar school group for TMUA practice, supporting Year 12 and 13 students preparing for competitive quantitative admissions tests and Oxbridge interviews.",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "TMUA Practice"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Deployed across schools within the independent school group for TMUA practice, supporting Year 12 and 13 students preparing for competitive quantitative admissions tests and Oxbridge interviews.",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "TMUA Practice"
    }
  ];

  const faqs = [
    {
      q: "Which universities require the TMUA for 2026/2027 entry?",
      a: ["The TMUA (Test of Mathematics for University Admission) is required or strongly recommended by Cambridge (Computer Science & Economics), Oxford, Imperial College London (CS & Maths), LSE, and Warwick for entry into competitive quantitative degrees."]
    },
    {
      q: "How is the TMUA scored and converted to the 1.0 to 9.0 scale?",
      a: ["Raw scores from Paper 1 (20 questions) and Paper 2 (20 questions) are combined and mapped onto a non-linear 1.0 to 9.0 scale. A score of 6.5+ typically represents the top 15% of test-takers."]
    },
    {
      q: "How does the TMUA Exam Simulator work on ivori?",
      a: ["Our simulator mirrors the official computer-based exam format. You get timed Paper 1 and Paper 2 practice tests, complete with full step-by-step mathematical proof breakdowns for every single question."]
    },
    {
      q: "What is the difference between Paper 1 and Paper 2?",
      a: ["Paper 1 tests your ability to apply core secondary mathematics (algebra, calculus, geometry) to novel problems. Paper 2 tests formal logic, necessary & sufficient conditions, proof by contradiction, and flaw detection in mathematical arguments."]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes! All subscriptions can be managed or cancelled in 1-click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'tmua.ivoriedu.com';
  const onIvoriAiEdu = host === 'tmua.edu.ivoriai.com';
  const tmuaUrl = onIvoriEdu
    ? 'https://tmua.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://tmua.edu.ivoriai.com/'
      : (host.includes('tmua') ? 'https://tmua.edu.ivoriai.com/' : 'https://ivoriai.com/edu/tmua');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="tmua-landing-root">
      <SeoMeta
        title="TMUA Exam Prep 2026 - Paper 1 and Paper 2 TMUA-Style Mock Papers"
        description="Master the TMUA (Test of Mathematics for University Admission) for Cambridge, Oxford, Imperial, LSE, and Warwick with ivori timed computer exam simulations, Paper 1 & Paper 2 past papers, and step-by-step logic proof breakdowns."
        keywords="TMUA exam prep, TMUA practice test, TMUA paper 1 paper 2, Cambridge math test, Imperial TMUA, tmua past papers, ivori simulator, ivori tmua, tmua score conversion, cambridge computer science tmua cut off, tmua logic proofs, tmua paper 2 worked solutions, lse tmua requirements, warwick tmua morse, tmua formula cheat sheet, tmua past papers free 2026"
        url={tmuaUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "TMUA Mathematics Admissions Test Preparation",
                "description": "Master the Test of Mathematics for University Admission (TMUA) for Cambridge, Oxford, Imperial, LSE, and Warwick with ivori timed computer-based exam simulations, Paper 1 & Paper 2 past papers, and step-by-step logic proof breakdowns.",
                "provider": {
                  "@type": "Organization",
                  "name": "ivori AI",
                  "url": eduHomeUrl
                },
                "url": tmuaUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "Paper 1 Mathematical Applications & Problem Solving",
                  "Paper 2 Mathematical Reasoning & Formal Logic Proofs",
                  "Evaluating Necessary vs. Sufficient Mathematical Conditions",
                  "Proof by Contrapositive and Negation of Statements"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "TMUA Preparation", "item": tmuaUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="exam-landing" />

      {/* HERO */}
      <section className="tmua-hero-section">
        <div className="tmua-container tmua-hero-content">
          <span className="tmua-mono-eyebrow">IVORI Edu</span>
          <h1 className="tmua-hero-title">Master the TMUA</h1>
          <p className="tmua-hero-tagline">Train the Mathematical Thinking the TMUA Rewards.</p>
          <p className="tmua-hero-sub">Realistic ivori timed mocks for Paper 1 &amp; Paper 2, a targeted quantitative reasoning question bank, and step-by-step proof explanations.</p>
          <button type="button" className="tmua-hero-cta" onClick={handleExplorePlans}>
            Explore TMUA Plans
          </button>
        </div>
      </section>

      {/* ADMISSIONS GUIDE */}
      <section id="tmua-matrix" className="tmua-editorial-section">
        <div className="tmua-container">
          <div className="tmua-editorial-section-head">
            <span className="tmua-mono-eyebrow">Target Universities · Official Entry Benchmarks</span>
            <h2>UK University TMUA Admissions Matrix for 2026/2027 Entry</h2>
            <p>Score targets and requirement status across leading Computer Science &amp; Economics departments.</p>
          </div>

          <ScrollCarousel ariaLabel="university">
            {admissionsRequirements.map((row, idx) => (
              <div className="tmua-matrix-card" key={idx}>
                <div className="tmua-matrix-uni">{row.uni}</div>
                <div className="tmua-matrix-row"><span>Degree Program</span><b>{row.course}</b></div>
                <div className="tmua-matrix-row"><span>Requirement</span><b className="tmua-req-badge">{row.requirement}</b></div>
                <div className="tmua-matrix-row tmua-score-cell"><span>Target Score</span><b>{row.target}</b></div>
                <div className="tmua-matrix-row"><span>Admissions Note</span><b>{row.details}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* HOW IT WORKS / IVORI METHOD */}
      <section className="tmua-editorial-section">
        <div className="tmua-container">
          <div className="tmua-editorial-section-head">
            <span className="tmua-mono-eyebrow">How It Works</span>
            <h2>Utilize IVORI Edu</h2>
            <p>Learn through Pólya's Heuristic Method and Lakatos's Logic of Proof</p>
          </div>

          <ScrollCarousel ariaLabel="section">
            {structureCards.map((card, idx) => (
              <div className="tmua-structure-card" key={idx}>
                <span className="tmua-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="tmua-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE QUESTION */}
      <section className="tmua-editorial-section">
        <div className="tmua-container">
          <div className="tmua-editorial-section-head">
            <span className="tmua-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how ivori explains a Paper 1 problem and breaks down a Paper 2 proof, step by step.</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="tmua-qcard-panel">
              <span className="tmua-mono-tag">Paper 1 · Applications</span>
              <div className="tmua-passage">
                The equation x² − (k + 3)x + 3k = 0 has two distinct real roots for every value of k except one.
              </div>
              <h3>For which value of k does the equation NOT have two distinct real roots?</h3>
            </div>

            <div className="tmua-qcard-panel">
              <span className="tmua-mono-tag">Answer Options</span>
              <div className="tmua-option">A) k = 0</div>
              <div className="tmua-option correct">B) k = 3</div>
              <div className="tmua-option">C) k = −3</div>
              <div className="tmua-option">D) k = 6</div>
            </div>

            <div className="tmua-qcard-panel">
              <span className="tmua-mono-tag">Step-by-Step Working</span>
              <p>1. Two distinct real roots require the discriminant Δ = b² − 4ac to be strictly positive.</p>
              <p>2. Here a = 1, b = −(k + 3), c = 3k, so Δ = (k + 3)² − 4(3k) = k² + 6k + 9 − 12k = k² − 6k + 9.</p>
              <p>3. Factorizing: k² − 6k + 9 = (k − 3)².</p>
              <p>4. Since (k − 3)² ≥ 0 for every real k, Δ = 0 only when k = 3, and Δ &gt; 0 for every other value of k.</p>
              <p>5. Therefore the equation fails to have two distinct real roots only when k = 3 (Option B).</p>
            </div>

            <div className="tmua-qcard-panel">
              <span className="tmua-mono-tag">Paper 2 · Logic &amp; Proof</span>
              <div className="tmua-passage">
                Let P(n) be the statement: "If n² + 1 is divisible by 5, then n ends in 2 or 3."
              </div>
              <h3>Which of the following statements represents the contrapositive of P(n)?</h3>
            </div>

            <div className="tmua-qcard-panel">
              <span className="tmua-mono-tag">Answer Options</span>
              <div className="tmua-option">A) If n ends in 2 or 3, then n² + 1 is divisible by 5.</div>
              <div className="tmua-option">B) If n does not end in 2 or 3, then n² + 1 is not divisible by 5.</div>
              <div className="tmua-option correct">C) If n ends in 0, 1, 4, 5, 6, 7, 8, or 9, then n² + 1 is not divisible by 5.</div>
              <div className="tmua-option">D) If n² + 1 is not divisible by 5, then n does not end in 2 or 3.</div>
            </div>

            <div className="tmua-qcard-panel">
              <span className="tmua-mono-tag">Step-by-Step Logic Breakdown</span>
              <p>1. A statement of the form A ⇒ B has the contrapositive ¬B ⇒ ¬A.</p>
              <p>2. Here, A = "n² + 1 is divisible by 5," and B = "n ends in 2 or 3."</p>
              <p>3. The negation ¬B is: "n does not end in 2 or 3" — meaning n ends in any other digit (0, 1, 4, 5, 6, 7, 8, 9).</p>
              <p>4. The negation ¬A is: "n² + 1 is not divisible by 5."</p>
              <p>5. Therefore, the contrapositive is: "If n ends in 0, 1, 4, 5, 6, 7, 8, or 9, then n² + 1 is not divisible by 5." (Option C.)</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="tmua-editorial-section">
        <div className="tmua-container">
          <div className="tmua-editorial-section-head">
            <span className="tmua-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools</h2>
            <p>Deployed across top UK grammar and independent schools with TMUA cohorts</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="tmua-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="tmua-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="tmua-school-more">
                  Find out more about{' '}
                  <button type="button" className="tmua-inline-link tmua-link-btn" onClick={() => navigate('/cases')}>
                    {school.name}
                  </button>
                </p>
                <div className="tmua-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="tmua-editorial-section tmua-pricing-editorial">
        <div className="tmua-container">
          <div className="tmua-editorial-section-head">
            <span className="tmua-mono-eyebrow">Subscription Plans</span>
            <h2>Unlock Unlimited TMUA Practice</h2>
            <p>Get instant access to all past papers, full mock exams, and AI proof explanations.</p>
            {!mayPurchase && <p>{ORG_PLAN_NOTICE}</p>}
          </div>

          <div className="tmua-pricing-grid">
            <div className="tmua-plan">
              <h3>Beginnings</h3>
              <div className="tmua-plan-price">
                <span className="amount">£0</span> <span className="period">free forever</span>
              </div>
              <button type="button" className="tmua-plan-btn" onClick={() => handleSubscribe('TMUA Beginnings')}>
                Get Started
              </button>
              <ul>
                <li><span className="tmua-check">·</span> <b>All Content for Free</b></li>
                <li><span className="tmua-check">·</span> 8 Official Tests / 320 Questions</li>
                <li><span className="tmua-check">·</span> 4 Easy Tests / 160 Questions</li>
                <li><span className="tmua-check">·</span> 50+ Investment Banking &amp; Computer Science Cases &amp; Tutorials</li>
              </ul>
            </div>

            <div className="tmua-plan">
              <h3>Foundations</h3>
              <div className="tmua-plan-price">
                <span className="amount">£60</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="tmua-plan-btn" onClick={() => handleSubscribe('TMUA Foundations')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="tmua-check">·</span> <b>All content in Beginnings</b></li>
                <li><span className="tmua-check">·</span> 6 Standard Tests / 240 Questions</li>
                <li><span className="tmua-check">·</span> 100+ Numerical, Logical, Pattern and Verbal Reasoning</li>
                <li><span className="tmua-check">·</span> UCAS Personal Statement Reviewer</li>
              </ul>
            </div>

            <div className="tmua-plan featured">
              <span className="tmua-plan-badge">Recommended · Oxbridge</span>
              <h3>Beyond</h3>
              <div className="tmua-plan-price">
                <span className="amount">£80</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="tmua-plan-btn" onClick={() => handleSubscribe('TMUA Beyond')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="tmua-check">·</span> <b>All content in Foundations</b></li>
                <li><span className="tmua-check">·</span> 2 Hard Tests / 80 Questions</li>
                <li><span className="tmua-check">·</span> CV and Cover Letter Builder</li>
                <li><span className="tmua-check">·</span> 16 Oxbridge Interview Simulations on tmua.edu.ivoriai.com</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="tmua-editorial-section tmua-faq-editorial">
        <div className="tmua-container">
          <div className="tmua-editorial-section-head">
            <span className="tmua-mono-eyebrow">Got Questions?</span>
            <h2>Frequently Asked Questions</h2>
            <p>Find answers on prep tiers, scoring and how IVORI's feedback works</p>
          </div>

          <div className="tmua-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`tmua-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="tmua-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="tmua-faq-plus">+</span>
                </div>
                <div className="tmua-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Free Lead Capture Modal (preserved, currently unused below — see note above) */}
      {showLeadModal && (
        <div className="lead-modal-overlay" onClick={() => setShowLeadModal(false)}>
          <div className="lead-modal-card" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="lead-modal-close" onClick={() => setShowLeadModal(false)}>✕</button>
            <h3 className="lead-modal-title">Start Free TMUA Beginnings</h3>
            <p className="lead-modal-sub">
              Enter your email to unlock 16 TMUA digital past papers, interactive test environment, and score diagnostics.
            </p>
            <form onSubmit={handleLeadSubmit}>
              <input
                type="email"
                className="lead-input"
                placeholder="Enter your email address"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                required
                autoFocus
              />
              <button type="submit" className="lead-submit-btn">
                Continue to Free TMUA Practice
              </button>
            </form>
            <div className="lead-divider">or sign up with</div>
            <div className="lead-sso-group">
              <button type="button" className="lead-sso-btn" onClick={() => navigate('/login?signup=true')}>
                Google
              </button>
              <button type="button" className="lead-sso-btn" onClick={() => navigate('/login?signup=true')}>
                LinkedIn
              </button>
            </div>
          </div>
        </div>
      )}

      <TmuaEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}